/* import __COLOCATED_TEMPLATE__ from './hitl-options.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

type Option = 'on' | 'off';

const MIN_THRESHOLD = 10;

interface Signature {
  Args: {
    assignableAdmins: $TSFixMe;
    selectedOption: Option;
    setSelectedOption: (option: Option) => void;
    selectedThreshold: number;
    setSelectedThreshold: (threshold: number) => void;
    selectedAssigneeId: string | null;
    setAssigneeId: (id: string) => void;
    selectedTimeoutSeconds: number;
    setTimeoutSeconds: (seconds: number) => void;
    isInteractionDisabled: boolean;
    showVolumeHelper: boolean;
    estimatedConversationsPerDay: number;
    minTimeoutSeconds: number;
    maxTimeoutSeconds: number;
    predicateGroup?: $TSFixMe;
    onPredicateGroupChange?: (predicates: $TSFixMe) => void;
    attributeGroupList?: $TSFixMe[];
    useCollapsibleThreshold?: boolean;
  };
}

export default class HitlOptions extends Component<Signature> {
  get isEnabled(): boolean {
    return this.args.selectedOption === 'on';
  }

  // Fallback layout: track represents the 10–100 range, so threshold=10 maps to 0% visual
  get normalizedPercent(): number {
    return (
      Math.round(((this.args.selectedThreshold - MIN_THRESHOLD) / (100 - MIN_THRESHOLD)) * 10000) /
      100
    );
  }

  // New pill layout: track spans 0–100 visually, thumb drag-locked at MIN by parent clamp
  get expandedSliderStyle(): string {
    let pct = this.args.selectedThreshold;
    return sanitizeHtml(
      `background: linear-gradient(to right, var(--neutral-fill) 0%, var(--neutral-fill) ${pct}%, var(--neutral-container-emphasis) ${pct}%, var(--neutral-container-emphasis) 100%)`,
    );
  }

  get expandedTooltipStyle(): string {
    let pct = this.args.selectedThreshold;
    return sanitizeHtml(`left: ${pct}%; top: 50%; transform: translate(-${pct}%, -50%);`);
  }

  get options() {
    return [
      {
        value: 'off' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.description',
      },
      {
        value: 'on' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.description',
      },
    ];
  }

  get thresholdSliderStyle(): string {
    let pct = this.normalizedPercent;
    return sanitizeHtml(
      `background: linear-gradient(to right, var(--neutral-fill) 0%, var(--neutral-fill) ${pct}%, var(--neutral-container-emphasis) ${pct}%, var(--neutral-container-emphasis) 100%)`,
    );
  }

  get thresholdTooltipStyle(): string {
    let pct = this.normalizedPercent;
    return sanitizeHtml(`left: ${pct}%; top: 50%; transform: translate(-${pct}%, -50%);`);
  }

  @tracked isThresholdExpanded = false;

  @action
  toggleThresholdExpanded(): void {
    this.isThresholdExpanded = !this.isThresholdExpanded;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::AskATeammate::HitlOptions': typeof HitlOptions;
    'operator/fin/setup/sections/ask-a-teammate/hitl-options': typeof HitlOptions;
  }
}
